import React, { forwardRef, useCallback } from 'react';

import { Loading } from '@rover/icons';
import { Box, BoxProps, Flex, Text } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';

import { throwOrLogError } from '../../utils';
import {
  FlexboxSystemProps,
  LayoutSystemProps,
  PaddingSystemProps,
} from '../styledSystemPropTypes';

import {
  ButtonCommonProps,
  ButtonIconMarginMap,
  ButtonIconSizeMap,
  ButtonSizeTextSizeMap,
  ButtonYPaddingMap,
  getFullWidthPropForContainer,
  getVariant,
  getXPadding,
  InternalButtonVariants,
} from './Button.common';

export type Props = ButtonCommonProps &
  PaddingSystemProps &
  LayoutSystemProps &
  FlexboxSystemProps & {
    /** ARIA label for accessibility when button has no visible text https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label */
    'aria-label'?: string;
    /** QA automation identifier */
    'data-qa-id'?: string;
    /** Click event handler with web types, take care using the event object in hybrid contexts! */
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    /** Tab order index for keyboard navigation */
    tabIndex?: number;
    /** Button type attribute (affects form submission) */
    type?: 'button' | 'submit' | 'reset';

    /** AVOID USING, supports a legacy use case in UnwrappedFileUploadButton */
    className?: string;

    /** (WEB ONLY) Added to support some edge cases on web */
    onBlur?: React.FocusEventHandler<HTMLButtonElement>;
    /** (WEB ONLY) Added to support some edge cases on web */
    onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
    /** (WEB ONLY) HTML id prop */
    id?: string;
    /** (WEB ONLY) HTML form prop for associating the button with a form https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/form */
    form?: string;

    ref?: never;
  };

// A copypaste of the original Rebass Button impl using our Box component
type ButtonBaseProps = Omit<BoxProps, 'variant'> & {
  variant?: InternalButtonVariants;
  children?: React.ReactNode;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  href?: string;
  webAs: 'a' | 'button';
};
const ButtonBase = forwardRef<HTMLElement, ButtonBaseProps>((props, ref) => (
  <Box
    {...props}
    ref={ref}
    tx="buttons"
    variant={props.variant}
    __css={{
      appearance: 'none',
      display: 'inline-block',
      textAlign: 'center',
      lineHeight: 'inherit',
      textDecoration: 'none',
      fontSize: 'inherit',
      px: 3,
      py: 2,
      color: 'white',
      bg: 'primary',
      border: 0,
      borderRadius: 4,
    }}
  />
));

const ButtonIcon = ({
  icon: Icon,
  // the destructuring of flexShrink prevents errors from it being passed to an <svg />
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  flexShrink,
  ...other
}: {
  icon: React.ComponentType<unknown>;
  flexShrink: string;
}): JSX.Element => {
  return <Icon {...other} />;
};

function Button({
  children,
  contrast = false,
  circular = false,
  destructive = false,
  disabled = false,
  fullWidth = false,
  href,
  hrefTarget,
  icon,
  iconAlignment = 'left',
  isSelected = false,
  loading = false,
  onClick,
  selectedIcon,
  size = 'default',
  type = 'button',
  variant = 'default',
  justifyContent = 'center',
  sx,
  webRef,
  roleOverride,
  ...other
}: Props): JSX.Element {
  if (!children && !other['aria-label'] && !other['aria-labelledby']) {
    throwOrLogError('Button.props MUST have children, or a11y values');
  }
  if (selectedIcon && icon && iconAlignment !== 'right') {
    throwOrLogError(
      'Button.props selectedIcon with a static icon is only supported with iconAlignment="right"'
    );
  }
  if (
    isSelected &&
    !['aria-checked', 'aria-pressed', 'aria-selected'].some((p) => Object.keys(other).includes(p))
  ) {
    throwOrLogError(
      'Button.props isSelected requires one of aria-checked, aria-pressed, or aria-selected to be true'
    );
  }

  const isLink = Boolean(href);
  const theme = useTheme();

  // Get theme color for links to override browser defaults
  const buttonTextColor = (() => {
    if (!isLink) return '';
    const variantKey = getVariant(disabled, destructive, contrast, variant);
    const [variantName, stateName] = variantKey.split('.');
    const buttonTheme = theme.buttons?.[variantName]?.[stateName] as { color?: string } | undefined;
    return buttonTheme?.color || '';
  })();

  const handleClick = useCallback(
    async (e) => {
      // if no onClick handler or we are not on the client, allow normal href behavior
      if (!onClick || (!process.env.JS_ENV_CLIENT && process.env.NODE_ENV !== 'test')) return;
      // prevent default behavior of navigating to href
      e.preventDefault();
      // await the onClick handler before navigating to href
      try {
        await onClick(e);
      } finally {
        if (isLink && href !== '') window.open(href, hrefTarget || '_self');
      }
    },
    [onClick, href, isLink, hrefTarget]
  );

  const commonIconProps = {
    fill: 'currentColor',
    height: ButtonIconSizeMap[size],
    width: ButtonIconSizeMap[size],
  };

  const styledIcon = icon ? (
    <Box
      {...commonIconProps}
      icon={icon}
      webAs={ButtonIcon}
      flexShrink="0"
      data-testid="kc-button-icon"
    />
  ) : undefined;

  const styling = isLink
    ? {
        '&, &:link, &:visited, &:focus, &:hover, &:active': {
          'text-decoration': 'none !important',
          color: `${buttonTextColor} !important`,
        },
      }
    : {};

  return (
    <ButtonBase
      variant={getVariant(disabled, destructive, contrast, variant)}
      disabled={disabled}
      px={getXPadding(size, circular)}
      py={ButtonYPaddingMap[size]}
      type={isLink ? undefined : type}
      webAs={isLink ? 'a' : 'button'}
      role={roleOverride ?? (isLink ? 'link' : 'button')}
      href={disabled || !isLink ? undefined : href}
      target={disabled || !isLink ? undefined : hrefTarget}
      onClick={handleClick}
      {...getFullWidthPropForContainer(fullWidth)}
      sx={{ outlineOffset: '3px', ...styling, ...sx }}
      ref={webRef}
      {...other}
    >
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent={justifyContent}
        gap={other.childrenFlexGap || ButtonIconMarginMap[size]}
      >
        {(!circular || !loading) && icon && iconAlignment === 'left' && styledIcon}
        {(!circular || !loading) && isSelected && selectedIcon && (
          <Box
            {...commonIconProps}
            icon={selectedIcon}
            webAs={ButtonIcon}
            flexShrink="0"
            data-testid="kc-button-selectedicon"
          />
        )}
        {children && (
          <Text
            size={ButtonSizeTextSizeMap[size]}
            fontWeight="semibold"
            data-testid="kc-button-text"
          >
            {children}
          </Text>
        )}
        {(!circular || !loading) && icon && iconAlignment === 'right' && styledIcon}
        {loading && (
          <Box
            flexShrink="0"
            height={ButtonIconSizeMap[size]}
            width={ButtonIconSizeMap[size]}
            data-testid="kibble-button-loading-spinner"
          >
            <Loading {...commonIconProps} />
          </Box>
        )}
      </Flex>
    </ButtonBase>
  );
}

/**
 * Button is an interactive component that triggers actions when clicked.
 * Supports loading states, icons, variants, links, and accessibility features.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/3836346394/Kibble+Core+Button
 */
export default Button;
